import { StatesModule, Transition } from '@uirouter/angular';
import { Ng2StateDeclaration } from '@uirouter/angular';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ChecklistListNavigationTabsComponent } from '@fi-app/checklist/list/checklist-list-navigation-tabs/checklist-list-navigation-tabs.component';
import { ChecklistListViewComponent } from '@fi-app/checklist/list/checklist-list-view/checklist-list-view.component';
import { FiChecklistViewComponent } from '@fi-app/checklist/checklist-view/checklist-view.component';
import { ChecklistService } from '@fi-app/checklist/checklist.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { AppTextService } from '@fi-shared/app-text';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { FiChecklistEditComponent } from '@fi-app/checklist/edit/edit.component';
import { FiChecklistCheckpointAnswerViewStateComponent } from '@fi-app/checklist/checkpoint-answer-view-state/checkpoint-answer-view-state.component';
import { IChecklistListStateModel } from '@fi-app/checklist/checklist';
import { ListData } from '@fi-app/components/items-list/list-data.service';
import { BehaviorSubject } from 'rxjs';
import { IFiChecklistViewerModel } from '@fi-shared/checklist';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiChecklistService } from '@fi-shared/fi-shared-services';
import { FiChecklist2EditComponent } from '@fi-shared/checklist/fi-checklist2-edit/fi-checklist2-edit.component';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiChecklistStatesService } from './fi-checklist-states.service';

const ChecklistState: Ng2StateDeclaration = {
    name: FiStateNames.Checklist._name,
    url: '/Checklist',
    abstract: true,
    component: ChecklistListNavigationTabsComponent,
    onEnter: (transition: Transition, state: Ng2StateDeclaration) => {
        const injector = transition.injector();
        const fiChecklistStatesService = injector.get(FiChecklistStatesService);
        fiChecklistStatesService.showChecklistModuleRemovalWarningDialog();
    },
};

const ChecklistListState: Ng2StateDeclaration = {
    name: FiStateNames.Checklist.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    component: ChecklistListViewComponent,
    reloadOnSearch: false,
    resolve: {
        pageTitle: [PageTitleService, ChecklistList_pageTitle],
        checklistListStateModel: [ChecklistList_checklistListStateModel],
    },
};
export function ChecklistList_checklistListStateModel(): IChecklistListStateModel {
    return {
        listData$: new BehaviorSubject<ListData<any>>(null),
    };
}
export function ChecklistList_pageTitle(ccmPageTitle: PageTitleService) {
    return ccmPageTitle.setTitleByItem(ObjectTypes.CheckList);
}

const ChecklistViewState: Ng2StateDeclaration = {
    name: FiStateNames.Checklist.View,
    url: '/{Id:[0-9]+}/View/{onlyActive}?:filterType',
    params: {
        onlyActive: null,
        fromSystemNotifications: null,
    },
    views: {
        '@Module': {
            component: FiChecklistViewComponent,
        },
    },
    resolve: {
        checklist: ['checklistViewer', ChecklistView_checklist],
        unlockedCheckpoint: [FiStateService, Transition, 'checklist', ChecklistView_unlockedCheckpoint],
        checklistViewer: [
            FiChecklistService,
            FiDialogsService,
            AppTextService,
            LoadingService,
            FiStateService,
            Transition,
            ChecklistView_checklistViewer,
        ],
        pageTitle: [PageTitleService, 'checklist', ChecklistView_PageTitle],
        fromSystemNotifications: [FiStateService, Transition, ChecklistView_fromSystemNotifications],
    },
    data: {
        stateClass: 'fi-state-mobile-no-toolbar',
    },
};
export function ChecklistView_checklist(checklistViewer: IFiChecklistViewerModel) {
    return checklistViewer.CheckList;
}
export function ChecklistView_unlockedCheckpoint(fiStateService: FiStateService, $transition$: Transition, checklist) {
    return checklist.CheckPoints[fiStateService.getParams($transition$).checkpoint];
}
export function ChecklistView_checklistViewer(
    checklistService: FiChecklistService,
    fiDialogsService: FiDialogsService,
    appText: AppTextService,
    fiLoadingService: LoadingService,
    stateService: FiStateService,
    transition: Transition,
) {
    const promise = new Promise<IFiChecklistViewerModel>((resolve, reject) => {
        fiLoadingService.trackPromise(
            checklistService.getCheckListViewer(stateService.getParams(transition).Id).then(function (data) {
                let checklist = data.CheckList;
                if (stateService.getParams(transition).onlyActive && !checklist.IsActive) {
                    fiDialogsService
                        .notify({
                            messageText: appText.get('checklist.not_activated_warning'),
                        })
                        .onYesButtonClick(function () {
                            resolve(data);
                        });
                } else {
                    resolve(data);
                }
            }, reject),
        );
    });
    return promise;
}
export function ChecklistView_PageTitle(ccmPageTitle: PageTitleService, checklist: ChecklistModel) {
    return ccmPageTitle.setTitle(checklist.Name);
}
export function ChecklistView_fromSystemNotifications(fiStateService: FiStateService, transition: Transition) {
    return fiStateService.getParams(transition).fromSystemNotifications;
}

const ChecklistAddState: Ng2StateDeclaration = {
    name: FiStateNames.Checklist.Add,
    url: '/Add',
    views: {
        '@Module': {
            component: FiChecklistEditComponent,
        },
    },
    resolve: {
        pageTitle: [PageTitleService, 'checklist', ChecklistAdd_pageTitle],
        checklist: [ChecklistService, ChecklistAdd_checklist],
    },
};

export function ChecklistAdd_pageTitle(ccmPageTitle: PageTitleService, checklist: ChecklistModel) {
    return ccmPageTitle.setTitleByItem(checklist);
}
export function ChecklistAdd_checklist(checklistService: ChecklistService) {
    return checklistService.getChecklistInstance({ CheckPoints: [{}] });
}

const ChecklistEditState: Ng2StateDeclaration = {
    name: FiStateNames.Checklist.Edit,
    url: '/{Id:[0-9]+}/Edit',
    views: {
        '@Module': {
            component: FiChecklistEditComponent,
        },
    },
    resolve: {
        checklist: [FiStateService, Transition, ChecklistService, LoadingService, ChecklistEdit_checklist],
        pageTitle: [PageTitleService, 'checklist', ChecklistEdit_pageTitle],
    },
};

export function ChecklistEdit_checklist(
    stateService: FiStateService,
    transition: Transition,
    checklistService: ChecklistService,
    fiLoadingService: LoadingService,
) {
    return fiLoadingService.trackPromise(
        checklistService.getCheckList(stateService.getParams(transition).Id, (data) =>
            checklistService.getChecklistInstance(data),
        ),
    );
}
export function ChecklistEdit_pageTitle(ccmPageTitle: PageTitleService, checklist: ChecklistModel) {
    return ccmPageTitle.setTitleByItem(checklist);
}

const ChecklistViewCheckpointAnswerState: Ng2StateDeclaration = {
    name: FiStateNames.Checklist.ViewCheckpointAnswer,
    url: '/ViewCheckpointAnswer/:checklistId/:checkpointAnswerId',
    views: {
        '@Module': {
            component: FiChecklistCheckpointAnswerViewStateComponent,
        },
    },
    resolve: {
        checklistViewer: [ChecklistService, FiStateService, Transition, ChecklistViewCheckpointAnswer_checklistViewer],
        checklist: ['checklistViewer', ChecklistViewCheckpointAnswer_checklist],
    },
};
export function ChecklistViewCheckpointAnswer_checklistViewer(
    checklistService: ChecklistService,
    stateService: FiStateService,
    transition: Transition,
) {
    return checklistService.getCheckListViewerByAnswerId(
        stateService.getParams(transition).checklistId,
        stateService.getParams(transition).checkpointAnswerId,
    );
}
export function ChecklistViewCheckpointAnswer_checklist(checklistViewer: IFiChecklistViewerModel) {
    return checklistViewer.CheckList;
}

export function getCheckListModulesForLocalSearch() {
    return [
        FiStateNames.Checklist.List,
        FiStateNames.Checklist.View,
        FiStateNames.Checklist.Edit,
        FiStateNames.Checklist.Add,
    ];
}

export const ChecklistStates: StatesModule = {
    states: [
        ChecklistState,
        ChecklistListState,
        ChecklistViewState,
        ChecklistAddState,
        ChecklistEditState,
        ChecklistViewCheckpointAnswerState,
    ],
};

export function registerChecklistStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules([FiStateNames.Checklist._name], {
        SettingType: [ApplicationSettingsType.ChecklistLocal],
        AccessType: AccessRightType.Read,
    });

    stateAccessCheckService.registerStateRules([FiStateNames.Checklist.List, FiStateNames.Checklist.View], {
        SettingType: [ApplicationSettingsType.ChecklistLocal],
        AccessType: AccessRightType.Read,
    });

    stateAccessCheckService.registerStateRules([FiStateNames.Checklist.Report.List], {
        SettingType: [ApplicationSettingsType.CheckListReports],
        AccessType: AccessRightType.Write,
    });
}
